import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { payrollApi } from '../axios';

// ─── Company Payroll Settings ─────────────────────────────────────────────────

export const usePayrollSettings = () => useQuery({
  queryKey: ['payroll', 'settings'],
  queryFn: () => payrollApi.get('/v1/payroll/settings').then(r => r.data),
  staleTime: 60000,
});

export const useUpdatePayrollSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => payrollApi.put('/v1/payroll/settings', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll', 'settings'] }),
  });
};

// ─── Salary Structures ────────────────────────────────────────────────────────

export const useSalaryStructures = (params) => useQuery({
  queryKey: ['payroll', 'salary-structures', params],
  queryFn: () => payrollApi.get('/v1/payroll/salary-structures', { params }).then(r => r.data),
  staleTime: 30000,
});

export const useEmployeeSalaryStructure = (employeeId) => useQuery({
  queryKey: ['payroll', 'salary-structures', 'employee', employeeId],
  queryFn: () => payrollApi.get(`/v1/payroll/salary-structures/${employeeId}`).then(r => r.data),
  enabled: !!employeeId,
  staleTime: 30000,
  retry: false,
});

export const useCreateSalaryStructure = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => payrollApi.post('/v1/payroll/salary-structures', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll', 'salary-structures'] }),
  });
};

export const useUpdateSalaryStructure = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) => payrollApi.put(`/v1/payroll/salary-structures/${id}`, payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll', 'salary-structures'] }),
  });
};

export const useDeleteSalaryStructure = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => payrollApi.delete(`/v1/payroll/salary-structures/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll', 'salary-structures'] }),
  });
};

export const usePreviewSalaryStructure = () => useMutation({
  mutationFn: (payload) => payrollApi.post('/v1/payroll/salary-structures/preview', payload).then(r => r.data),
});

// ─── Payroll Runs ─────────────────────────────────────────────────────────────

export const usePayrollRuns = (params) => useQuery({
  queryKey: ['payroll', 'runs', params],
  queryFn: () => payrollApi.get('/v1/payroll/runs', { params }).then(r => r.data),
  staleTime: 30000,
});

export const usePayrollRun = (runId) => useQuery({
  queryKey: ['payroll', 'run', runId],
  queryFn: () => payrollApi.get(`/v1/payroll/runs/${runId}`).then(r => r.data),
  enabled: !!runId,
  staleTime: 30000,
});

export const useRunPayroll = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => payrollApi.post('/v1/payroll/runs', payload).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
};

export const useMarkPayrollPaid = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId) => payrollApi.post(`/v1/payroll/runs/${runId}/mark-paid`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll'] }),
  });
};

export const useDeletePayrollRun = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId) => payrollApi.delete(`/v1/payroll/runs/${runId}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll', 'runs'] }),
  });
};

// ─── Payslip ─────────────────────────────────────────────────────────────────

export const usePayslip = (runId, itemId) => useQuery({
  queryKey: ['payroll', 'payslip', runId, itemId],
  queryFn: () => payrollApi.get(`/v1/payroll/runs/${runId}/items/${itemId}`).then(r => r.data),
  enabled: !!runId && !!itemId,
  staleTime: 60000,
});
